import { createSelector } from 'reselect';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { type PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { type StoreState } from '../types/StoreState';
import { type PaymentMethodFundingSource } from '../types/PaymentsButtonViewConfig';
import { selectAvailableMethods, selectAvailablePayPalFundingSources } from './payment';

const selectMethods = (state: StoreState) => state.domain.paymentMethods;

export const selectMethodsMetadata = createSelector(selectMethods, (methods) => {
  return methods.map((method) => {
    const { paymentProviderId, ...rest } = method;

    return {
      methodId: Object.keys(rest)[0] as PaymentMethod,
      providerId: paymentProviderId,
    };
  });
});

export const selectPayPalMetadata = createSelector(selectMethodsMetadata, (meta) =>
  meta.find((method) => method.methodId === PaymentMethod.PayPal),
);

export const selectGooglePayMetadata = createSelector(selectMethodsMetadata, (meta) =>
  meta.find((method) => method.methodId === PaymentMethod.GooglePay),
);

const SORT_ORDER: PaymentMethod[] = [PaymentMethod.ApplePay, PaymentMethod.PayPal, PaymentMethod.GooglePay];

export const selectAvailableFundingSourcesMetadata = createSelector(
  selectMethodsMetadata,
  selectAvailableMethods,
  selectAvailablePayPalFundingSources,
  (methodsMetadata, availableMethods, availablePayPalFundingSources) =>
    methodsMetadata
      .slice()
      .sort((a, b) => SORT_ORDER.indexOf(a.methodId) - SORT_ORDER.indexOf(b.methodId))
      .reduce<{ methodId: PaymentMethodFundingSource; providerId: PaymentProvider }[]>((acc, method) => {
        if (availableMethods.includes(method.methodId)) {
          if (method.methodId === PaymentMethod.PayPal) {
            acc.push(
              ...availablePayPalFundingSources.map((fundingSource) => ({
                methodId: fundingSource,
                providerId: method.providerId,
              })),
            );
          } else {
            acc.push(
              method as { methodId: PaymentMethod.ApplePay | PaymentMethod.GooglePay; providerId: PaymentProvider },
            );
          }
        }

        return acc;
      }, []),
);
